import React, { useEffect, useMemo, useState } from "react";
import {
  FiActivity,
  FiCalendar,
  FiRefreshCw,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";

import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import pt from "date-fns/locale/pt";

import Footer from "../../../components/Footer/Footer";
import { GraficoBarra } from "../../../components/Graficos/GraficoBarra";
import { GraficoLinha } from "../../../components/Graficos/GraficoLinha";
import { LoadingButton } from "../../../components/loadingButtom";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";
import Navbar from "../../../components/Navbar/Navbar";
import { SelectCliente } from "../../../components/select/SelectCliente";
import { SelectServicoFinanceiro } from "../../../components/select/SelectServicoFinanceiro";
import {
  gerarCoresAleatorias,
  handleGraficosRankingClientes,
  obterIdFundoFinanceiroAssessoria,
  obterIdFundoFinanceiroPrivate,
} from "./functions";
import "./DashboardsGestor.css";

registerLocale("pt", pt);

type DashboardPanelProps = {
  title: string;
  eyebrow: string;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  children: React.ReactNode;
};

const formatarNumero = (valor?: number, sufixo = "") => {
  if (valor === undefined || Number.isNaN(valor)) {
    return "-";
  }

  return `${new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(valor)}${sufixo}`;
};

const DashboardPanel = ({
  title,
  eyebrow,
  loading,
  error,
  onRetry,
  children,
}: DashboardPanelProps) => (
  <section className="gestor-dashboard-panel">
    <div className="gestor-dashboard-panel-header">
      <div>
        <span>{eyebrow}</span>
        <h2>{title}</h2>
      </div>
      {error && (
        <button type="button" onClick={onRetry}>
          <FiRefreshCw />
          Recarregar
        </button>
      )}
    </div>

    {loading ? (
      <div className="gestor-dashboard-state">
        <LoadingButton />
      </div>
    ) : error ? (
      <div className="gestor-dashboard-state error">
        <strong>Não foi possível carregar este gráfico.</strong>
      </div>
    ) : (
      children
    )}
  </section>
);

const DashboardsGestor = () => {
  const [modalDesconectar, setModalDesconectar] = useState<boolean>(false);
  const [dataInicial, setDataInicial] = useState<Date>(
    new Date(new Date().setMonth(new Date().getMonth() - 60))
  );
  const [dataFinal, setDataFinal] = useState<Date>(new Date());
  const [rendimentosDia, setRendimentosDia] = useState<Array<number>>([]);
  const [valoresTotais, setValoresTotais] = useState<Array<number>>([]);
  const [valoresAcumulados, setValoresAcumulados] = useState<Array<number>>([]);
  const [labels, setLabels] = useState<Array<string>>([]);
  const [indexData, setIndexData] = useState<number>(1);
  const [indexDataPrivate, setIndexDataPrivate] = useState<number>(1);
  const [porcentagensRanking, setPorcentagensRanking] = useState<Array<number>>([]);
  const [labelsRanking, setLabelsRanking] = useState<Array<string>>([]);
  const [loadingPerformance, setLoadingPerformance] = useState<boolean>(true);
  const [errorPerformance, setErrorPerformance] = useState<boolean>(false);
  const [loadingRanking, setLoadingRanking] = useState<boolean>(true);
  const [errorRanking, setErrorRanking] = useState<boolean>(false);
  const [reloadPerformance, setReloadPerformance] = useState<boolean>(true);
  const [reloadRanking, setReloadRanking] = useState<boolean>(true);
  const [servico, setServico] = useState<number>(1);
  const [idCliente, setIdCliente] = useState<string>("");

  useEffect(() => {
    if (servico === 1) {
      setIdCliente("");
      obterIdFundoFinanceiroAssessoria(
        setLoadingPerformance,
        setErrorPerformance,
        dataInicial,
        dataFinal,
        setModalDesconectar,
        setRendimentosDia,
        setValoresTotais,
        setLabels,
        setValoresAcumulados
      );
      return;
    }

    if (idCliente) {
      obterIdFundoFinanceiroPrivate(
        setLoadingPerformance,
        setErrorPerformance,
        dataInicial,
        dataFinal,
        setModalDesconectar,
        setRendimentosDia,
        setValoresTotais,
        setLabels,
        setValoresAcumulados,
        idCliente
      );
    } else {
      setLoadingPerformance(false);
      setErrorPerformance(false);
      setRendimentosDia([]);
      setValoresTotais([]);
      setValoresAcumulados([]);
      setLabels([]);
    }
  }, [dataInicial, dataFinal, reloadPerformance, idCliente, servico]);

  useEffect(() => {
    setIdCliente("");
  }, [servico]);

  useEffect(() => {
    handleGraficosRankingClientes(
      setLoadingRanking,
      setErrorRanking,
      setModalDesconectar,
      setPorcentagensRanking,
      setLabelsRanking
    );
  }, [reloadRanking]);

  const coresRanking = useMemo(
    () => gerarCoresAleatorias(1, []).slice(0, 10),
    []
  );
  const ultimoRetornoMensal = valoresTotais[valoresTotais.length - 1];
  const ultimoAcumulado = valoresAcumulados[valoresAcumulados.length - 1];
  const maiorCliente = labelsRanking[0] || "-";
  const tituloServico = servico === 1 ? "Assessoria" : "Private";
  const aguardandoCliente = servico !== 1 && !idCliente;

  const recarregarPerformance = () => setReloadPerformance((atual) => !atual);
  const recarregarRanking = () => setReloadRanking((atual) => !atual);

  return (
    <>
      <Navbar />

      <main className="gestor-dashboard-page">
        <section className="gestor-dashboard-hero">
          <div className="gestor-dashboard-hero-copy">
            <span>Inteligência gerencial</span>
            <h1>Dashboards</h1>
            <p>
              Acompanhe performance, rendimento acumulado e ranking de cotas em
              uma visão operacional para a gestão Omex Capital.
            </p>
          </div>

          <div className="gestor-dashboard-metrics" aria-label="Resumo do dashboard">
            <div>
              <FiActivity />
              <span>Retorno mensal</span>
              <strong>{formatarNumero(ultimoRetornoMensal, "%")}</strong>
            </div>
            <div>
              <FiTrendingUp />
              <span>Acumulado</span>
              <strong>{formatarNumero(ultimoAcumulado, "%")}</strong>
            </div>
            <div>
              <FiUsers />
              <span>Maior posição</span>
              <strong>{maiorCliente}</strong>
            </div>
          </div>
        </section>

        <section className="gestor-dashboard-toolbar" aria-label="Filtros do dashboard">
          <label>
            <span>Serviço</span>
            <SelectServicoFinanceiro
              className="gestor-dashboard-select"
              setServico={setServico}
            />
          </label>

          {servico !== 1 && (
            <label>
              <span>Cliente</span>
              <SelectCliente
                className="gestor-dashboard-select"
                setIdCliente={setIdCliente}
                setErrorGrafico={setErrorPerformance}
                setLoadingGrafico={setLoadingPerformance}
                setModalDesconectar={setModalDesconectar}
                idServicoFinanceiro={servico}
              />
            </label>
          )}

          <label>
            <span>Data inicial</span>
            <DatePicker
              className="gestor-dashboard-input"
              maxDate={new Date(new Date().setDate(new Date().getDate() - 1))}
              dateFormat="dd/MM/yyyy"
              locale="pt"
              selected={dataInicial}
              onChange={(dataInicio: Date) => setDataInicial(dataInicio)}
            />
          </label>

          <label>
            <span>Data final</span>
            <DatePicker
              className="gestor-dashboard-input"
              maxDate={new Date()}
              minDate={dataInicial}
              dateFormat="dd/MM/yyyy"
              locale="pt"
              selected={dataFinal}
              onChange={(dataFim: Date) => setDataFinal(dataFim)}
            />
          </label>

          <button type="button" onClick={recarregarPerformance} disabled={loadingPerformance}>
            <FiCalendar />
            Aplicar
          </button>
        </section>

        <section className="gestor-dashboard-context">
          <div>
            <span>Visão ativa</span>
            <strong>{tituloServico}</strong>
          </div>
          <div>
            <span>Pontos no período</span>
            <strong>{labels.length}</strong>
          </div>
          <div>
            <span>Ranking monitorado</span>
            <strong>{labelsRanking.length}</strong>
          </div>
        </section>

        {aguardandoCliente ? (
          <section className="gestor-dashboard-empty">
            <FiUsers />
            <strong>Selecione um cliente Private para exibir a performance.</strong>
          </section>
        ) : (
          <div className="gestor-dashboard-grid">
            <DashboardPanel
              title={`Performance mensal ${tituloServico}`}
              eyebrow="Evolução"
              loading={loadingPerformance}
              error={errorPerformance}
              onRetry={recarregarPerformance}
            >
              <GraficoLinha
                setIndexData={setIndexData}
                dadosGrafico={valoresTotais}
                labels={labels}
                nomeDado="Evolução"
                tituloGrafico={`Performance mensal ${tituloServico}`}
                tituloTooltip="Diferença percentual mensal"
                dadoTooltip={`Valor percentual: ${formatarNumero(
                  valoresTotais[indexData],
                  "%"
                )}`}
                graficoPorcentagem
              />
            </DashboardPanel>

            <DashboardPanel
              title={`Rendimento acumulado ${tituloServico}`}
              eyebrow="Acumulado"
              loading={loadingPerformance}
              error={errorPerformance}
              onRetry={recarregarPerformance}
            >
              <GraficoLinha
                setIndexData={setIndexDataPrivate}
                dadosGrafico={servico === 1 ? valoresAcumulados : rendimentosDia}
                labels={labels}
                nomeDado="Evolução"
                tituloGrafico={`Rendimento acumulado ${tituloServico}`}
                tituloTooltip="Valor acumulado percentual"
                dadoTooltip={`Valor acumulado: ${formatarNumero(
                  servico === 1
                    ? valoresAcumulados[indexDataPrivate]
                    : rendimentosDia[indexDataPrivate],
                  "%"
                )}`}
                graficoPorcentagem
              />
            </DashboardPanel>
          </div>
        )}

        <DashboardPanel
          title="Ranking de cotas"
          eyebrow="Top 10"
          loading={loadingRanking}
          error={errorRanking}
          onRetry={recarregarRanking}
        >
          <GraficoBarra
            porcentagensDado={porcentagensRanking.slice(0, 10)}
            labels={labelsRanking.slice(0, 10)}
            nomeDado="Posição em cotas"
            tituloGrafico="Ranking de cotas"
            colorArray={coresRanking}
          />
        </DashboardPanel>
      </main>

      <Footer />
      {modalDesconectar && <ModalDesconectar modal={modalDesconectar} />}
    </>
  );
};

export default DashboardsGestor;
